import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { arrayMove } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import type {
  useUpdateBooking,
  useAddBookingService,
  useRemoveBookingService,
  useReorderBookingServices,
} from '@/hooks/useBookings';
import { useNotify } from '@/stores/notificationStore';
import { ApiError } from '@/lib/api';
import { describeApiError } from '@/lib/describeApiError';

type UpdateBookingMutation = ReturnType<typeof useUpdateBooking>;
type AddBookingServiceMutation = ReturnType<typeof useAddBookingService>;
type RemoveBookingServiceMutation = ReturnType<typeof useRemoveBookingService>;
type ReorderBookingServicesMutation = ReturnType<typeof useReorderBookingServices>;

interface UseWorkOrderServiceActionsArgs {
  workOrder: WorkOrderDetail | undefined;
  updateBooking: UpdateBookingMutation;
  addService: AddBookingServiceMutation;
  removeService: RemoveBookingServiceMutation;
  reorderServices: ReorderBookingServicesMutation;
  setActiveDragId: (id: string | null) => void;
  setExpandedServiceIds: (ids: Set<string>) => void;
}

export interface WorkOrderServiceActions {
  handleAddService: (serviceId: string, mechanicId?: string | null, laborItemIds?: string[] | null) => Promise<void>;
  handleRemoveService: (bookingServiceId: string) => Promise<void>;
  handleServiceMechanicChange: (bookingServiceId: string, mechanicId: string | null) => Promise<void>;
  handleDragStart: (event: { active: { id: string | number } }) => void;
  handleReorderServices: (event: DragEndEvent) => Promise<void>;
}

export function useWorkOrderServiceActions({
  workOrder,
  updateBooking,
  addService,
  removeService,
  reorderServices,
  setActiveDragId,
  setExpandedServiceIds,
}: UseWorkOrderServiceActionsArgs): WorkOrderServiceActions {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  // A refusal must say it is a permission problem, not a generic failure:
  // "Päivitys epäonnistui" sent a JV supervisor into undoing a status (#3305).
  const notifyEditError = useCallback(
    (error: unknown) => {
      const message =
        error instanceof ApiError && error.status === 403
          ? t('editForbidden')
          : describeApiError(error, tc, { fallback: t('editFailed') });
      notify.error(tc('error'), message);
    },
    [notify, t, tc],
  );

  const handleRemoveService = useCallback(
    async (bookingServiceId: string) => {
      if (!workOrder) return;

      // Use the surgical delete endpoint — NOT the `serviceIds` replace on
      // updateBooking. A full replace recreates the kept rows and resets their
      // status and per-service progress (#885).
      try {
        await removeService.mutateAsync({
          id: workOrder.id,
          bookingServiceId,
        });
        notify.success(t('editSuccess'), t('editSuccessDesc'));
      } catch (error) {
        notifyEditError(error);
      }
    },
    [workOrder, removeService, notify, notifyEditError, t],
  );

  const handleAddService = useCallback(
    async (serviceId: string, mechanicId?: string | null, laborItemIds?: string[] | null) => {
      if (!workOrder) return;

      // Use the surgical additive endpoint — NOT the `serviceIds` replace on
      // updateBooking. A full replace deletes and recreates every booking_services
      // row, resetting the status and per-service progress of works already on the
      // order (#860).
      try {
        await addService.mutateAsync({
          id: workOrder.id,
          serviceId,
          mechanicId: mechanicId ?? null,
          ...(laborItemIds != null ? { laborItemIds } : {}),
        });
        notify.success(t('editSuccess'), t('editSuccessDesc'));
      } catch (error) {
        notifyEditError(error);
        throw error;
      }
    },
    [workOrder, addService, notify, notifyEditError, t],
  );

  const handleServiceMechanicChange = useCallback(
    async (bookingServiceId: string, mechanicId: string | null) => {
      if (!workOrder) return;

      // Send only `serviceMechanics` (no `serviceIds`) to take the backend's
      // surgical update path. Sending `serviceIds` triggers a destructive
      // replace that wipes per-service labor items / parts / extras (#251).
      // Map keys must be `bookingServiceId`, not catalog `serviceId`.
      const target = workOrder.services.find((s) => s.bookingServiceId === bookingServiceId);
      if (!target) return;

      try {
        await updateBooking.mutateAsync({
          id: workOrder.id,
          data: {
            serviceMechanics: { [target.bookingServiceId]: mechanicId },
          },
        });
        notify.success(t('editSuccess'), t('editSuccessDesc'));
      } catch (error) {
        notifyEditError(error);
      }
    },
    [workOrder, updateBooking, notify, notifyEditError, t],
  );

  const handleDragStart = useCallback(
    (event: { active: { id: string | number } }) => {
      setActiveDragId(String(event.active.id));
      setExpandedServiceIds(new Set());
    },
    [setActiveDragId, setExpandedServiceIds],
  );

  const handleReorderServices = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveDragId(null);

      if (!over || active.id === over.id || !workOrder) return;

      const oldIndex = workOrder.services.findIndex((s) => s.bookingServiceId === active.id);
      const newIndex = workOrder.services.findIndex((s) => s.bookingServiceId === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(workOrder.services, oldIndex, newIndex);
      const orderedBookingServiceIds = reordered.map((s) => s.bookingServiceId);

      // Use the surgical reorder endpoint — NOT the `serviceIds` replace on
      // updateBooking, which recreates every row and resets status/progress (#885).
      try {
        await reorderServices.mutateAsync({
          id: workOrder.id,
          orderedBookingServiceIds,
        });
      } catch (error) {
        notifyEditError(error);
      }
    },
    [workOrder, reorderServices, setActiveDragId, notifyEditError],
  );

  return {
    handleAddService,
    handleRemoveService,
    handleServiceMechanicChange,
    handleDragStart,
    handleReorderServices,
  };
}
